import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { setBreadcrumbItems } from "../../store/actions";
import axios from "../../utils/axios";
import Swal from 'sweetalert2';
import {
    Button, Modal, ModalHeader, ModalBody, ModalFooter, Table,
    Form, FormGroup, Label, Input, Row, Col, Card, CardBody, CardImg, Badge, CardTitle,
    Spinner, InputGroup, InputGroupText
} from 'reactstrap';
import { toast } from 'react-toastify';
import moment from 'moment';
import { Link } from 'react-router-dom';
import { FiCircle, FiCheckCircle, FiEdit, FiTrash2, FiEye, FiMoreVertical, FiUpload, FiPlus, FiX } from 'react-icons/fi';

const EventCategories = (props) => {
    document.title = "Event Categories | Equity Circle - Admin Dashboard";
    const breadcrumbItems = [
        { title: "Sessions Management", link: "#" },
        { title: "Event Categories", link: "#" },
    ];

    // State
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteModal, setDeleteModal] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);

    // Fetch categories
    const fetchCategories = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/event-categories');
            setCategories(response.data.data || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Failed to load event categories');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        props.setBreadcrumbItems('Sessions Categories', breadcrumbItems);
        fetchCategories();
    }, [fetchCategories]);

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Toggle modal
    const toggleModal = (category = null) => {
        setCurrentCategory(category);
        if (category) {
            setFormData({
                name: category.name,
                description: category.description || ''
            });
        } else {
            setFormData({
                name: '',
                description: ''
            });
        }
        setModal(!modal);
    };

    // Toggle delete confirmation modal
    const toggleDeleteModal = (category = null) => {
        setCategoryToDelete(category);
        setDeleteModal(!deleteModal);
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (currentCategory) {
                // Update existing category
                await axios.put(`/api/event-categories/${currentCategory.id}`, formData);
                toast.success('Category updated successfully');
            } else {
                // Create new category
                await axios.post('/api/event-categories', formData);
                toast.success('Category created successfully');
            }

            fetchCategories();
            toggleModal();
        } catch (error) {
            console.error('Error saving category:', error);
            const errorMessage = error.response?.data?.message || 'Failed to save category';
            toast.error(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle delete category
    const handleDelete = async () => {
        if (!categoryToDelete) return;

        try {
            await axios.delete(`/api/event-categories/${categoryToDelete.id}`);
            toast.success('Category deleted successfully');
            fetchCategories();
        } catch (error) {
            console.error('Error deleting category:', error);
            const errorMessage = error.response?.data?.message || 'Failed to delete category';
            toast.error(errorMessage);
        } finally {
            toggleDeleteModal();
        }
    };

    // Filter categories based on search term
    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (category.description && category.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const createEventCategory = () => toggleModal();

    return (
        <React.Fragment>
            <Row>
                <Col className="col-12">
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <CardTitle className="h4 mb-0">Session Categories</CardTitle>
                                <div className="d-flex justify-content-end align-items-center gap-2">
                                    <InputGroup style={{ maxWidth: '500px', minWidth: '350px', width: '100%' }}>
                                        <Input
                                            type="text"
                                            placeholder="Search categories..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                    <Button color="primary" style={{ minWidth: 'fit-content' }} onClick={createEventCategory}>
                                        <i className="bx bx-plus me-1"></i> Add New Category
                                    </Button>
                                </div>
                            </div>

                            {/* <Row className="mb-4">
                                <Col md={4}>
                                    <InputGroup>
                                        <InputGroupText>
                                            <i className="bx bx-search"></i>
                                        </InputGroupText>
                                        <Input
                                            type="text"
                                            placeholder="Search categories..."
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                        />
                                    </InputGroup>
                                </Col>
                            </Row> */}

                            {loading ? (
                                <div className="text-center my-5">
                                    <Spinner color="primary" />
                                    <p className="mt-2">Loading categories...</p>
                                </div>
                            ) : filteredCategories.length > 0 ? (
                                <div className="table-responsive">
                                    <Table className="table-centered table-nowrap mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>#</th>
                                                <th>Name</th>
                                                <th>Slug</th>
                                                <th>Description</th>
                                                <th>Created At</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCategories.map((category, index) => (
                                                <tr key={category.id}>
                                                    <td>{index + 1}</td>
                                                    <td>
                                                        <strong>{category.name}</strong>
                                                    </td>
                                                    <td>
                                                        <Badge color="light" className="font-size-12">
                                                            {category.slug}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        {category.description ? (
                                                            <span className="text-muted">
                                                                {category.description.length > 50
                                                                    ? `${category.description.substring(0, 50)}...`
                                                                    : category.description}
                                                            </span>
                                                        ) : (
                                                            <span className="text-muted">No description</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        {moment(category.created_at).format('MMM D, YYYY')}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            <Button
                                                                color="primary"
                                                                className="p-1 px-2"
                                                                onClick={() => toggleModal(category)}
                                                            >
                                                                <FiEdit size={16} />
                                                            </Button>
                                                            {/* <Button 
                                                                color="danger" 
                                                                size="sm" 
                                                                className="btn-sm"
                                                                onClick={() => toggleDeleteModal(category)}
                                                            >
                                                                <i className="bx bx-trash"></i>
                                                            </Button> */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="text-center py-5">
                                    <i className="bx bx-package display-4 text-muted mb-3"></i>
                                    <h5>No categories found</h5>
                                    <p className="text-muted">
                                        {searchTerm ? 'No categories match your search' : 'Get started by creating a new category'}
                                    </p>
                                    {!searchTerm && (
                                        <Button color="primary" onClick={createEventCategory}>
                                            <i className="bx bx-plus me-1"></i> Add Category
                                        </Button>
                                    )}
                                </div>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* Add/Edit Category Modal */}
            <Modal isOpen={modal} toggle={() => toggleModal()} centered>
                <ModalHeader toggle={() => toggleModal()}>
                    {currentCategory ? 'Edit Category' : 'Add New Category'}
                </ModalHeader>
                <Form onSubmit={handleSubmit}>
                    <ModalBody>
                        <FormGroup className="mb-3">
                            <Label>Name <span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                placeholder="Enter category name"
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input
                                type="textarea"
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Enter category description (optional)"
                                rows="3"
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            type="button"
                            color="light"
                            onClick={() => toggleModal()}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                    {currentCategory ? 'Updating...' : 'Creating...'}
                                </>
                            ) : currentCategory ? (
                                'Update Category'
                            ) : (
                                'Create Category'
                            )}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal isOpen={deleteModal} toggle={toggleDeleteModal} centered>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Deletion</ModalHeader>
                <ModalBody>
                    <p>Are you sure you want to delete the category <strong>{categoryToDelete?.name}</strong>? This action cannot be undone.</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="light" onClick={toggleDeleteModal}>
                        Cancel
                    </Button>
                    <Button color="danger" onClick={handleDelete}>
                        <i className="bx bx-trash me-1"></i> Delete
                    </Button>
                </ModalFooter>
            </Modal>
        </React.Fragment>
    );
}

export default connect(null, { setBreadcrumbItems })(EventCategories);